import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../services/api';
import type { AppDispatch } from './index';
import type { ShiftGroup } from '../types/shiftGroups';

export type { ShiftGroup };

interface ShiftGroupsState {
  shiftGroups: ShiftGroup[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ShiftGroupsState = {
  shiftGroups: [],
  isLoading: false,
  error: null,
};

const shiftGroupsSlice = createSlice({
  name: 'shiftGroups',
  initialState,
  reducers: {
    setShiftGroups(state, action: PayloadAction<ShiftGroup[]>) {
      state.shiftGroups = action.payload;
    },
    upsertShiftGroup(state, action: PayloadAction<ShiftGroup>) {
      const idx = state.shiftGroups.findIndex(
        (g) => g.shiftGroupId === action.payload.shiftGroupId && g.editPointDate === action.payload.editPointDate
      );
      if (idx !== -1) {
        state.shiftGroups[idx] = action.payload;
      } else {
        state.shiftGroups.push(action.payload);
      }
    },
    removeShiftGroupsByGroupId(state, action: PayloadAction<string>) {
      state.shiftGroups = state.shiftGroups.filter((g) => g.shiftGroupId !== action.payload);
    },
    removeShiftGroupEditPoint(
      state,
      action: PayloadAction<{ shiftGroupId: string; editPointDate: string }>
    ) {
      state.shiftGroups = state.shiftGroups.filter(
        (g) =>
          !(g.shiftGroupId === action.payload.shiftGroupId && g.editPointDate === action.payload.editPointDate)
      );
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const {
  setShiftGroups,
  upsertShiftGroup,
  removeShiftGroupsByGroupId,
  removeShiftGroupEditPoint,
  setLoading,
  setError,
} = shiftGroupsSlice.actions;

export const fetchShiftGroups =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await apiService.listShiftGroups();
      dispatch(setShiftGroups(response.data ?? []));
    } catch (error: any) {
      dispatch(setError(error?.message ?? 'Failed to load shift groups'));
    } finally {
      dispatch(setLoading(false));
    }
  };

export default shiftGroupsSlice.reducer;
